import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Building2, Percent, AlertCircle, CheckCircle, Plus, X } from "lucide-react";
import { toast } from "sonner";

const DEPARTMENTS = [
  { id: "PRQC", label: "PRQC", color: "bg-blue-100 text-blue-700" },
  { id: "LMDS", label: "LMDS", color: "bg-purple-100 text-purple-700" },
  { id: "LOCATION", label: "Location", color: "bg-amber-100 text-amber-700" },
  { id: "GLOBAL", label: "GLOBAL", color: "bg-slate-100 text-slate-700" }
];

export function VentilationManuelle({ 
  montant, 
  categorie,
  initialVentilation = null,
  onSave, 
  onCancel 
}) {
  const [ventilation, setVentilation] = useState(
    initialVentilation || [{ departement: "PRQC", pourcentage: 100 }]
  );
  const [error, setError] = useState("");

  useEffect(() => {
    validateTotal();
  }, [ventilation]);

  const validateTotal = () => {
    const total = ventilation.reduce((sum, v) => sum + parseFloat(v.pourcentage || 0), 0);
    if (Math.abs(total - 100) > 0.01) {
      setError(`Total: ${total.toFixed(1)}% (doit être 100%)`);
      return false;
    }
    setError("");
    return true;
  };

  const handlePourcentageChange = (index, value) => {
    const newVentilation = [...ventilation];
    newVentilation[index].pourcentage = parseFloat(value) || 0;
    setVentilation(newVentilation);
  };

  const handleDepartementChange = (index, dept) => {
    const newVentilation = [...ventilation];
    newVentilation[index].departement = dept;
    setVentilation(newVentilation);
  };

  const handleAddLine = () => {
    const usedDepts = new Set(ventilation.map(v => v.departement));
    const availableDept = DEPARTMENTS.find(d => !usedDepts.has(d.id));
    
    if (!availableDept) {
      toast.error("Tous les départements sont déjà utilisés");
      return;
    }

    setVentilation([...ventilation, { departement: availableDept.id, pourcentage: 0 }]);
  };

  const handleRemoveLine = (index) => {
    if (ventilation.length === 1) {
      toast.error("Au moins une ligne de ventilation est requise");
      return;
    }
    const newVentilation = ventilation.filter((_, i) => i !== index);
    setVentilation(newVentilation);
  };

  const handleSave = () => {
    if (!validateTotal()) {
      toast.error("Le total doit être exactement 100%");
      return;
    }

    const depts = ventilation.map(v => v.departement);
    const uniqueDepts = new Set(depts);
    if (depts.length !== uniqueDepts.size) {
      toast.error("Un département ne peut pas apparaître deux fois");
      return;
    }

    if (ventilation.some(v => v.pourcentage <= 0)) {
      toast.error("Tous les pourcentages doivent être supérieurs à 0");
      return;
    }

    onSave(ventilation);
  };

  const getDepartmentColor = (deptId) => {
    return DEPARTMENTS.find(d => d.id === deptId)?.color || "bg-slate-100";
  };

  const totalPourcentage = ventilation.reduce((sum, v) => sum + parseFloat(v.pourcentage || 0), 0);
  const isValid = Math.abs(totalPourcentage - 100) < 0.01;

  return (
    <Card className="border-2 border-blue-200 shadow-lg">
      <CardHeader className="bg-blue-50">
        <CardTitle className="text-base flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-600" />
          Ventilation Manuelle - {categorie}
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          Montant total: <span className="font-bold text-lg">{montant.toFixed(2)}$</span>
        </p>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="space-y-3">
          {ventilation.map((v, index) => {
            const montantDept = (montant * v.pourcentage) / 100;
            return (
              <div key={`ventilation-${v.departement || 'dept'}-${index}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                <div className="flex-1">
                  <Label className="text-xs mb-1">Département</Label>
                  <select
                    value={v.departement}
                    onChange={(e) => handleDepartementChange(index, e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  >
                    {DEPARTMENTS.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.label}</option>
                    ))}
                  </select>
                </div>

                <div className="w-28">
                  <Label className="text-xs mb-1">Pourcentage</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={v.pourcentage}
                      onChange={(e) => handlePourcentageChange(index, e.target.value)}
                      className="pr-8"
                    />
                    <Percent className="absolute right-2 top-2.5 h-4 w-4 text-slate-400" />
                  </div>
                </div>

                <div className="w-28">
                  <Label className="text-xs mb-1">Montant</Label>
                  <div className="p-2 bg-white border rounded text-sm font-semibold text-right">
                    {montantDept.toFixed(2)}$
                  </div>
                </div>

                <div className="flex-shrink-0">
                  <Badge className={getDepartmentColor(v.departement)}>
                    {v.departement}
                  </Badge>
                </div>

                {ventilation.length > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveLine(index)}
                    className="flex-shrink-0"
                  >
                    <X className="h-4 w-4 text-red-600" />
                  </Button>
                )}
              </div>
            );
          })}

          {ventilation.length < DEPARTMENTS.length && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddLine}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un département
            </Button>
          )}

          <div className={`p-4 rounded-lg border-2 ${isValid ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isValid ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
                <span className="font-semibold">Total:</span>
              </div>
              <div className="text-right">
                <span className={`text-2xl font-bold ${isValid ? 'text-green-700' : 'text-red-700'}`}>
                  {totalPourcentage.toFixed(1)}%
                </span>
                {error && (
                  <p className="text-xs text-red-600 mt-1">{error}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Annuler
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!isValid}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            Enregistrer la ventilation
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
